// UploadResearchDraft.jsx
import FileUpload from "components/FileUpload";
import { useAuth } from "contexts/AuthContext";
import { useToast } from "contexts/ToastContext";
import React, { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { updateInternalLegalResearchRequest, updateLegalResearchRequest } from "services/legalResearchService";
import { LEGAL_RESEARCH_REQUEST_STATUS } from "utils/constantValues";

interface uploadResearchDraftProps {
    request: any;
    isOpen: boolean;
    onClose: () => void;
    setLegalResearch: Function;
}

const UploadResearchDraft: React.FC<uploadResearchDraftProps> = ({
    request,
    isOpen,
    setLegalResearch,
    onClose,
}) => {
    const { t } = useTranslation();
    const [dataSummary, setDataSummary] = useState(request?.draft_summary || "");
    const [dataFile, setDataFile] = useState(
        new Date().toISOString().split("T")[0]
    );
    const contextData = useAuth();
    const { showToast } = useToast();
    const [submitting, setSubmitting] = useState(false);
    const [validationError, setValidationError] = useState<string | null>(null);

    const modalContentRef = useRef<HTMLDivElement>(null);

    // Prefill the summary with the existing draft summary when (re)opened so it can be edited.
    useEffect(() => {
        if (isOpen) setDataSummary(request?.draft_summary || "");
    }, [isOpen, request?.draft_summary]);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (
                modalContentRef.current &&
                !modalContentRef.current.contains(event.target as Node)
            ) {
                onClose();
            }
        };

        if (isOpen) {
            document.addEventListener("mousedown", handleClickOutside);
        }

        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [isOpen, onClose]);

    if (!isOpen) return null;

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        setValidationError(null);

        setSubmitting(true);

        try {
            const data = new FormData();
            data.append(
                "status",
                LEGAL_RESEARCH_REQUEST_STATUS.draft_submitted.value
            );
            data.append("draft_summary", dataSummary);
            data.append("research_draft_file", dataFile);
            let response: any;
            if (request.requested_by === "internal")
                response = await updateInternalLegalResearchRequest(contextData.token, request.id, data);
            else
                response = await updateLegalResearchRequest(request.id, data, contextData.token);
            setLegalResearch((prev: any) => ({
                ...prev,
                ...response,
            }));

            showToast(t("LegalResearchDraftUploaded"), "success");
        } catch (error) {
            console.error("Error submitting request:", error);
            showToast(t("FailedToUploadLegalResearchDraft"), "error");
        } finally {
            setSubmitting(false);
        }

        onClose();
    };

    const handleFileChange = (
        file: any,
    ) => {
        setDataFile(file);
    };

    return (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex justify-end p-4 z-50 overflow-hidden">
            <div
                ref={modalContentRef}
                className="bg-white rounded-lg shadow-xl w-full max-w-lg p-6 relative flex flex-col h-full my-auto overflow-y-auto pr-2 custom-scrollbar"
            >
                <button
                    onClick={onClose}
                    className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
                >
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-6 w-6"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                    >
                        <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M6 18L18 6M6 6l12 12"
                        />
                    </svg>
                </button>

                <h2 className="text-xl font-semibold text-gray-800 mb-6">
                    {t("UploadResearchDraft")}
                </h2>

                <form onSubmit={handleSubmit} className="flex flex-col">
                    <div className="flex-grow overflow-y-auto pr-2 custom-scrollbar">
                        <div className="mb-4">
                            <label
                                htmlFor="remark"
                                className="block text-sm font-medium text-gray-700 mb-2"
                            >
                                {t("DraftSummary")}
                            </label>
                            <textarea
                                id="data_summary"
                                className="w-full p-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-800 focus:border-transparent resize-y min-h-[100px]"
                                placeholder={t("DetailHere")}
                                value={dataSummary}
                                onChange={(e) => setDataSummary(e.target.value)}
                            ></textarea>
                        </div>

                        <div className="mb-6">
                            <label
                                htmlFor="deadline"
                                className="block text-sm font-medium text-gray-700 mb-2"
                            >
                                {t("DraftFile")}
                            </label>
                            <div className="relative">
                                <FileUpload onChange={handleFileChange} />
                            </div>
                        </div>

                    </div>
                    {validationError && (
                        <p className="text-red-600 text-sm mb-4">{validationError}</p>
                    )}
                    <div className="flex space-x-4 pt-4 border-t border-gray-200">
                        <button
                            type="submit"
                            className="w-full bg-gray-800 hover:bg-gray-900 text-white font-bold py-3 px-6 rounded-md flex items-center justify-center"
                            disabled={submitting}
                        >
                            {submitting ? (
                                <>
                                    <svg className="animate-spin -ml-1 mr-2 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                    </svg>
                                    {t("Submitting")}
                                </>
                            ) : (
                                t("Submit")
                            )}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};
export default UploadResearchDraft;